import {
  CalendarDays,
  ExternalLink,
  MoreHorizontal,
  PanelRightOpen,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import type { ThreadHistoryBoundaryResponse } from "@/hooks/api"

export interface ThreadHistoryBoundaryProps {
  boundary: ThreadHistoryBoundaryResponse
  workspaceId: string
  highlighted?: boolean
  onOpenSession: (historyId: number) => void
}

const ThreadHistoryBoundary = ({
  boundary,
  workspaceId,
  highlighted = false,
  onOpenSession,
}: ThreadHistoryBoundaryProps) => {
  const { t } = useTranslation()

  return (
    <div
      id={`thread-history-${boundary.history_id}`}
      className={cn(
        "my-5 scroll-mt-20 rounded-md border border-border/70 bg-muted/25 px-3 py-2 transition-colors",
        highlighted && "border-primary/30 bg-primary/5 ring-2 ring-primary/10"
      )}
      data-history-boundary={boundary.history_id}
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => onOpenSession(boundary.history_id)}
        >
          <div className="truncate text-xs font-semibold text-foreground">
            {boundary.title?.trim() ||
              t("historyList.thread.sessionTitle", {
                id: boundary.history_id,
              })}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              {new Date(boundary.started_at).toLocaleString()}
            </span>
            {boundary.agent_name && <span>{boundary.agent_name}</span>}
          </div>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              aria-label={t("historyList.thread.sessionActions")}
            >
              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => onOpenSession(boundary.history_id)}
            >
              <PanelRightOpen className="mr-2 h-4 w-4" aria-hidden="true" />
              {t("historyList.thread.viewSession")}
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                to={`/workspaces/${workspaceId}/histories/${boundary.history_id}`}
              >
                <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("historyList.detail.openFullPage")}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default ThreadHistoryBoundary
